import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";

const INPUT_CLASS =
  "w-full rounded-md border border-[#D5E2DB] bg-white px-4 py-3 text-sm text-[#1A221E] placeholder-[#4B5852]/50 outline-none transition-colors duration-300 focus:border-[#174C3A] focus:ring-2 focus:ring-[#174C3A]/10";

const LABEL_CLASS = "mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-[#174C3A]";

const initialForm = {
  nome: "",
  cognome: "",
  cellulare: "",
  email: "",
  messaggio: "",
  motivo: "visita",
  sede: "genova",
  consenso: false,
  consensoSalute: false,
  azienda: "",
};

export default function Valutazione() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [errore, setErrore] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErrore("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/richiesta-valutazione`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      setForm(initialForm);
      setSubmitted(true);
      toast.success("Richiesta inviata correttamente", {
        description: "La segreteria ti ricontatterà ai recapiti indicati.",
      });
    } catch {
      setErrore("Non è stato possibile inviare la richiesta. Riprova tra qualche minuto.");
      toast.error("Non è stato possibile inviare la richiesta", {
        description: "Riprova tra qualche minuto.",
      });
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setSubmitted(false);
  };

  return (
    <div data-testid="evaluation-page">
      <PageHero
        testid="evaluation-form-section"
        eyebrow="Contatto diretto"
        title="Richiedi una valutazione"
        subtitle="Descrivi brevemente la tua necessità. Lascia i tuoi recapiti e sarai ricontattato per ricevere maggiori informazioni sul percorso più appropriato."
      />

      <section className="bg-[#F8FAF8]">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          {submitted ? (
            <Reveal>
              <div
                data-testid="evaluation-success-panel"
                className="rounded-lg border border-[#2E6B50]/30 bg-white p-10 text-center shadow-sm sm:p-14"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F1EC] text-[#174C3A]">
                  <CheckCircle2 size={30} />
                </div>
                <h2 className="mt-6 font-serif text-2xl font-semibold text-[#0D3327] sm:text-3xl">
                  Richiesta inviata correttamente.
                </h2>
                <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4B5852]">
                  La segreteria ti ricontatterà ai recapiti indicati.
                </p>
                <button
                  type="button"
                  data-testid="evaluation-new-request-button"
                  onClick={reset}
                  className="mt-8 rounded-full border border-[#174C3A] px-7 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#174C3A] transition-colors duration-300 hover:bg-[#174C3A] hover:text-white"
                >
                  Compila una nuova richiesta
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form
                data-testid="evaluation-form"
                onSubmit={handleSubmit}
                className="rounded-lg border border-[#D5E2DB] bg-white p-7 shadow-sm sm:p-10"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nome" className={LABEL_CLASS}>Nome *</label>
                    <input
                      id="nome"
                      data-testid="input-patient-name"
                      type="text"
                      required
                      value={form.nome}
                      onChange={set("nome")}
                      placeholder="Il tuo nome"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label htmlFor="cognome" className={LABEL_CLASS}>Cognome *</label>
                    <input
                      id="cognome"
                      data-testid="input-patient-surname"
                      type="text"
                      required
                      value={form.cognome}
                      onChange={set("cognome")}
                      placeholder="Il tuo cognome"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label htmlFor="cellulare" className={LABEL_CLASS}>Cellulare *</label>
                    <input
                      id="cellulare"
                      data-testid="input-patient-phone"
                      type="tel"
                      required
                      value={form.cellulare}
                      onChange={set("cellulare")}
                      placeholder="+39 ..."
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className={LABEL_CLASS}>Email *</label>
                    <input
                      id="email"
                      data-testid="input-patient-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="nome@esempio.it"
                      className={INPUT_CLASS}
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label htmlFor="messaggio" className={LABEL_CLASS}>Descrivi brevemente la tua esigenza *</label>
                  <textarea
                    id="messaggio"
                    data-testid="input-patient-notes"
                    required
                    rows={6}
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    placeholder="Descrivi brevemente la tua necessità o il problema visivo per cui richiedi una valutazione..."
                    className={`${INPUT_CLASS} resize-y`}
                  />
                </div>

                <fieldset className="mt-8">
                  <legend className={LABEL_CLASS}>Per cosa desideri essere ricontattato?</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      { value: "chirurgia", label: "Chirurgia", testid: "radio-reason-surgery" },
                      { value: "visita", label: "Visita oculistica", testid: "radio-reason-visit" },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        data-testid={opt.testid}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3.5 text-sm font-medium transition-all duration-300 ${
                          form.motivo === opt.value
                            ? "border-[#174C3A] bg-[#E8F1EC] text-[#0D3327]"
                            : "border-[#D5E2DB] bg-white text-[#4B5852] hover:border-[#2E6B50]/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="motivo"
                          value={opt.value}
                          checked={form.motivo === opt.value}
                          onChange={set("motivo")}
                          className="h-4 w-4 accent-[#174C3A]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="mt-8">
                  <legend className={LABEL_CLASS}>Dove desideri essere seguito?</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      { value: "genova", label: "Genova", testid: "radio-location-genova" },
                      { value: "savona", label: "Savona", testid: "radio-location-savona" },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        data-testid={opt.testid}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3.5 text-sm font-medium transition-all duration-300 ${
                          form.sede === opt.value
                            ? "border-[#174C3A] bg-[#E8F1EC] text-[#0D3327]"
                            : "border-[#D5E2DB] bg-white text-[#4B5852] hover:border-[#2E6B50]/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="sede"
                          value={opt.value}
                          checked={form.sede === opt.value}
                          onChange={set("sede")}
                          className="h-4 w-4 accent-[#174C3A]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
                  <label htmlFor="azienda">Lascia questo campo vuoto</label>
                  <input
                    id="azienda"
                    data-testid="form-honeypot"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.azienda}
                    onChange={set("azienda")}
                  />
                </div>

                <div className="mt-8 space-y-4">
                  <label
                    data-testid="privacy-consent-checkbox"
                    className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#4B5852]"
                  >
                    <input
                      type="checkbox"
                      data-testid="privacy-consent-input"
                      checked={form.consenso}
                      onChange={(e) => setForm({ ...form, consenso: e.target.checked })}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#174C3A]"
                    />
                    <span>
                      Dichiaro di aver preso visione dell'{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="privacy-consent-link"
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-[#174C3A] underline decoration-[#174C3A]/40 underline-offset-2 transition-colors duration-300 hover:text-[#0D3327]"
                      >
                        Informativa Privacy
                      </a>
                      .
                    </span>
                  </label>
                  <label
                    data-testid="health-consent-checkbox"
                    className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#4B5852]"
                  >
                    <input
                      type="checkbox"
                      data-testid="health-consent-input"
                      checked={form.consensoSalute}
                      onChange={(e) => setForm({ ...form, consensoSalute: e.target.checked })}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#174C3A]"
                    />
                    <span>
                      Acconsento al trattamento dei dati relativi alla salute eventualmente desumibili dalle informazioni da me inserite nel modulo, esclusivamente per gestire la mia richiesta di ricontatto e di eventuale prenotazione.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  data-testid="submit-evaluation-button"
                  disabled={!form.consenso || !form.consensoSalute || sending}
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0D3327] px-8 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#174C3A] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[#0D3327] sm:w-auto"
                >
                  {sending ? "Invio in corso..." : "Invia la richiesta"} <Send size={15} />
                </button>
                {errore && (
                  <p
                    data-testid="form-error-message"
                    className="mt-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                  >
                    {errore}
                  </p>
                )}
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
